import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { AppShell } from "@/components/layout/AppShell";
import { getFirebaseAuth, isFirebaseConfigured } from "@/lib/firebase";

export const Route = createFileRoute("/signup")({
  head: () => ({
    meta: [
      { title: "Create account · PN-CivilServices" },
      {
        name: "description",
        content: "Create an account to bookmark and personalize your study.",
      },
      { property: "og:title", content: "Create account · PN-CivilServices" },
      {
        property: "og:description",
        content: "Join PN-CivilServices — free.",
      },
    ],
  }),
  component: Signup,
});

function Signup() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!isFirebaseConfigured()) {
      setErr("Firebase not configured yet.");
      return;
    }
    setBusy(true);
    try {
      const auth = getFirebaseAuth();
      if (!auth) throw new Error("Auth unavailable");
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (name) await updateProfile(cred.user, { displayName: name });
      nav({ to: "/" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Sign up failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-md px-4 pt-16 pb-24">
        <div className="label-card">Create account</div>
        <h1 className="mt-1 font-display text-3xl font-semibold">
          Start your own ledger.
        </h1>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-hairline bg-surface px-3 h-11 outline-none focus:ring-2 focus:ring-paprika"
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full rounded-md border border-hairline bg-surface px-3 h-11 outline-none focus:ring-2 focus:ring-paprika"
          />
          <input
            type="password"
            placeholder="Password (min 8)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            className="w-full rounded-md border border-hairline bg-surface px-3 h-11 outline-none focus:ring-2 focus:ring-paprika"
          />
          {err ? <div className="text-sm text-danger">{err}</div> : null}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-paprika py-2.5 text-sm font-semibold uppercase tracking-widest text-white disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>
        <div className="mt-6 text-sm text-ink-soft text-center">
          Have one already?{" "}
          <Link to="/login" className="text-paprika font-semibold">
            Sign in
          </Link>
        </div>
      </div>
    </AppShell>
  );
}
